export type ModerationStatusFilter = 'open' | 'reviewed' | 'dismissed' | 'all';
export type ModerationItemStatus = 'open' | 'reviewed' | 'dismissed' | 'escalated' | string;

export interface ModerationPerson {
  profileId: string;
  displayName: string;
  username: string | null;
  photoUrl: string | null;
}

export interface ModerationReport {
  id: string;
  roomCode: string | null;
  roomName: string | null;
  reporter: ModerationPerson | null;
  reported: ModerationPerson | null;
  category: string;
  body: string;
  status: ModerationItemStatus;
  createdAt: string | null;
  actionNotes: string | null;
}

export interface ModerationSafetySurvey {
  id: string;
  roomCode: string | null;
  roomName: string | null;
  model: ModerationPerson | null;
  feltSafe: boolean | null;
  note: string | null;
  status: ModerationItemStatus;
  createdAt: string | null;
}

export interface ModerationReview {
  id: string;
  roomCode: string | null;
  roomName: string | null;
  reviewer: ModerationPerson | null;
  subject: ModerationPerson | null;
  score: number | null;
  note: string | null;
  status: ModerationItemStatus;
  createdAt: string | null;
}

export interface ModerationDashboard {
  reports: ModerationReport[];
  safetySurveys: ModerationSafetySurvey[];
  reviews: ModerationReview[];
  totals: {
    openReports: number;
    unsafeSurveys: number;
    recentReviews: number;
  };
}

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: JsonRecord, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function readNumber(record: JsonRecord, keys: string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

function readBoolean(record: JsonRecord, keys: string[]): boolean | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return null;
}

function readArray(record: JsonRecord, keys: string[]): unknown[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

function normalizePerson(value: unknown): ModerationPerson | null {
  if (!isRecord(value)) return null;

  const profileId = readString(value, ['profileId', 'profile_id', 'id']);
  const displayName = readString(value, ['displayName', 'display_name', 'name']);
  if (!profileId || !displayName) return null;

  return {
    profileId,
    displayName,
    username: readString(value, ['username']),
    photoUrl: readString(value, ['photoUrl', 'photo_url', 'avatarUrl', 'avatar_url']),
  };
}

function normalizeReport(value: unknown): ModerationReport | null {
  if (!isRecord(value)) return null;
  const id = readString(value, ['id']);
  if (!id) return null;

  return {
    id,
    roomCode: readString(value, ['roomCode', 'room_code']),
    roomName: readString(value, ['roomName', 'room_name']),
    reporter: normalizePerson(value.reporter),
    reported: normalizePerson(value.reported) ?? normalizePerson(value.subject),
    category: readString(value, ['category']) ?? 'other',
    body: readString(value, ['body', 'note', 'description']) ?? '',
    status: readString(value, ['status']) ?? 'open',
    createdAt: readString(value, ['createdAt', 'created_at']),
    actionNotes: readString(value, ['actionNotes', 'action_notes']),
  };
}

function normalizeSafetySurvey(value: unknown): ModerationSafetySurvey | null {
  if (!isRecord(value)) return null;
  const id = readString(value, ['id']);
  if (!id) return null;

  return {
    id,
    roomCode: readString(value, ['roomCode', 'room_code']),
    roomName: readString(value, ['roomName', 'room_name']),
    model: normalizePerson(value.model),
    feltSafe: readBoolean(value, ['feltSafe', 'felt_safe']),
    note: readString(value, ['note']),
    status: readString(value, ['status']) ?? 'open',
    createdAt: readString(value, ['createdAt', 'created_at']),
  };
}

function normalizeReview(value: unknown): ModerationReview | null {
  if (!isRecord(value)) return null;
  const id = readString(value, ['id']);
  if (!id) return null;

  return {
    id,
    roomCode: readString(value, ['roomCode', 'room_code']),
    roomName: readString(value, ['roomName', 'room_name']),
    reviewer: normalizePerson(value.reviewer),
    subject: normalizePerson(value.subject),
    score: readNumber(value, ['score']),
    note: readString(value, ['note']),
    status: readString(value, ['status']) ?? 'open',
    createdAt: readString(value, ['createdAt', 'created_at']),
  };
}

export interface ModerationAction {
  id: string;
  actionType: string;
  sourceType: string;
  reason: string | null;
  createdAt: string | null;
  roomCode: string | null;
  roomName: string | null;
  actor: ModerationPerson | null;
  target: ModerationPerson | null;
}

function normalizeModerationAction(value: unknown): ModerationAction | null {
  if (!isRecord(value)) return null;
  const id = readString(value, ['id']);
  const actionType = readString(value, ['actionType', 'action_type']);
  if (!id || !actionType) return null;

  return {
    id,
    actionType,
    sourceType: readString(value, ['sourceType', 'source_type']) ?? 'unknown',
    reason: readString(value, ['reason']),
    createdAt: readString(value, ['createdAt', 'created_at']),
    roomCode: readString(value, ['roomCode', 'room_code']),
    roomName: readString(value, ['roomName', 'room_name']),
    actor: normalizePerson(value.actor),
    target: normalizePerson(value.target),
  };
}

export function normalizeModerationActions(payload: unknown): ModerationAction[] {
  if (!isRecord(payload)) return [];
  return readArray(payload, ['actions'])
    .map(normalizeModerationAction)
    .filter((item): item is ModerationAction => Boolean(item));
}

export function normalizeModerationDashboard(payload: unknown): ModerationDashboard | null {
  if (!isRecord(payload)) return null;
  const record = isRecord(payload.dashboard) ? payload.dashboard : payload;

  const reports = readArray(record, ['reports']).map(normalizeReport).filter((item): item is ModerationReport => Boolean(item));
  const safetySurveys = readArray(record, ['safetySurveys', 'safety_surveys'])
    .map(normalizeSafetySurvey)
    .filter((item): item is ModerationSafetySurvey => Boolean(item));
  const reviews = readArray(record, ['reviews']).map(normalizeReview).filter((item): item is ModerationReview => Boolean(item));

  const rawTotals = isRecord(record.totals) ? record.totals : {};

  return {
    reports,
    safetySurveys,
    reviews,
    totals: {
      openReports: readNumber(rawTotals, ['openReports', 'open_reports']) ?? reports.filter((report) => report.status === 'open').length,
      unsafeSurveys: readNumber(rawTotals, ['unsafeSurveys', 'unsafe_surveys'])
        ?? safetySurveys.filter((survey) => survey.feltSafe === false).length,
      recentReviews: readNumber(rawTotals, ['recentReviews', 'recent_reviews']) ?? reviews.length,
    },
  };
}
